import map from 'ramda/src/map'
import identity from 'ramda/src/identity'
import path from 'ramda/src/path'
import take from 'ramda/src/take'
import uniq from 'ramda/src/uniq'
import equals from 'ramda/src/equals'
import reduce from 'ramda/src/reduce'
import pathEq from 'ramda/src/pathEq'
import sort from 'ramda/src/sort'
import Typography, { typographyVariants } from 'root/src/client/web/typography/Typography'
import Icon from 'root/src/client/web/base/Icon'
import DareCardDrop from 'root/src/client/web/list/listItemComponents/dare/DareCardDrop'
import { colorsNew, dropShadow, xsMediaQuery } from 'root/src/client/web/commonStyles'
import { faTreasureChest } from '@fortawesome/pro-light-svg-icons/faTreasureChest'
import Slider from 'root/src/client/web/base/Slider'
import dropEventConnector from 'root/src/client/logic/dropEvent/connectors/dropEventConnector'
import Anchor from 'root/src/client/web/base/Anchor'
import anchors from 'root/src/shared/constants/anchors'
import { trackWindowScroll } from 'react-lazy-load-image-component'
import { dropEventStatusEnum, dropDareDifficultyLevelValue } from 'root/src/shared/constants/dropEvent'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import TooltipWraper, { tooltipPlacement } from 'root/src/client/web/base/TooltipWraper'
import { STREAMS_MINUTES_LIMIT } from 'root/src/shared/constants/campaignStreamingPointsRewards'

const { EXPIRED } = dropEventStatusEnum

const styles = {
	root: {
		width: 420,
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		[xsMediaQuery]: {
			width: '90%',
			margin: [[60, 'auto']],
		},
	},
	info: {
		display: 'flex',
		width: '100%',
		justifyContent: 'space-between',
	},
	infoColumn: {
		display: 'flex',
		flexDirection: 'column',
		flexGrow: 1,
	},
	infoItem: {
		display: 'flex',
		margin: [[0, 0, 20]],
		minHeight: 40,
		minWidth: 120,
		alignItems: 'center',
		gap: 10,
	},
	infoItemText: {
		display: 'flex',
		justifyContent: 'space-between',
		flexDirection: 'column',
	},
	card: {
		margin: [[5, 0, 0]],
	},
	slider: {
		transform: 'translateY(-50px)',
	},
	sliderContainer: {
		overflow: 'inherit',
	},
	sliderHeading: {
		display: 'block',
		marginBottom: '1.2em',
		filter: dropShadow(colorsNew.primary),
	},
	allDaresText: {
		cursor: 'pointer',
		textDecoration: 'none',
		borderBottom: `2px solid ${colorsNew.light3}`,
		'&:hover': {
			borderBottom: `2px solid ${colorsNew.light2}`,
		},
		'&:hover *': {
			color: colorsNew.light2,
		},
		padding: [38, 0, 0, 0],
	},
	link: {
		color: colorsNew.light3,
	},
	lootItem: {
		display: 'flex',
		flexWrap: 'wrap',
		paddingLeft: '1em',
		filter: dropShadow(colorsNew.primary),
	},
	remainingItems: {
		marginBottom: 35,
	},
	lootRow: {
		display: 'flex',
		flexDirection: 'column',
		margin: [[50, 0]],
		width: '100%',
	},
	lootGrid: {
		display: 'grid',
		flexWrap: 'wrap',
		gap: 1,
		gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
	},
}

const getPrizes = (availablePrizes, status, dares) => {
	const { lootRewards, initialProduct } = availablePrizes

	const { availableKeys, availableCodes } = lootRewards || {}
	const keysTextPart = availableKeys
		? `${availableKeys <= 100 ? availableKeys : '100+'} Keys`
		: ''
	const codesTextPart = availableCodes
		? `${availableCodes <= 100 ? availableCodes : '100+'} Codes`
		: ''
	const lootRewardKeysText =		keysTextPart
		+ (availableKeys && availableCodes ? ' and\n ' : '')
		+ codesTextPart

	const availableInitialProducts = reduce(
		(acc, product) => acc + product.available,
		0,
		initialProduct || [],
	)
	const initialProductType = pathEq([0, 'type'], 'dropKey', initialProduct)
		? 'Key'
		: 'Code'
	const initialRewardsKeysText = `${
		availableInitialProducts > 100 ? '100+' : availableInitialProducts
	} ${initialProductType}${availableInitialProducts > 1 ? 's' : ''}`

	const resultText = initialProduct
		? initialRewardsKeysText
		: lootRewardKeysText
	const remainingToWinText = !equals(status, EXPIRED) ? resultText : 0
	const daresDescendingDifficulty = sort(({ level: a }, { level: b }) => {
		if (dropDareDifficultyLevelValue[a] > dropDareDifficultyLevelValue[b]) return -1
		if (dropDareDifficultyLevelValue[a] < dropDareDifficultyLevelValue[b]) return 1
		return 0
	}, dares)
	return {
		remainingToWinText,
		loot: uniq(map(path(['product', 'name']), daresDescendingDifficulty)),
	}
}

const DaresSlider = ({
	classes, dares, game, campaignId, submitting, setSubmitting, availablePrizes, status,
	emailSubscription, toggleEmailSubscription, comingSoon, canAccessCreateBrandProject, pointsToEarn,
	canEarnPoints, showTermsModal, isAbleToParticipate,
}) => {
	const { loot } = getPrizes(availablePrizes, status, dares)
	return (
		<section className={classes.root}>
			<div className={classes.info}>
				<div className={classes.infoColumn}>
					<div className={classes.infoItemText}>
						<Typography
							variant={typographyVariants.mini}
							color={colorsNew.light4}
						>Drop Size
						</Typography>
						<Typography
							variant={typographyVariants.heading2}
						>
							{dares.length}{dares.length > 1 ? ' Dares' : ' Dare'}
						</Typography>
					</div>
				</div>
				<div className={classes.infoColumn}>
					{canEarnPoints && (
						<div className={classes.infoItemText} style={{ alignItems: 'flex-end' }}>
							<Typography
								variant={typographyVariants.mini}
								color={colorsNew.light4}
								style={{ display: 'flex' }}
							>
								Earn Up To
								<TooltipWraper
									tooltipHeader={
										`The points you earn for this drop are based on your most viewed ${STREAMS_MINUTES_LIMIT / 60} hour(s) of content. Using your average viewership, you will earn ${pointsToEarn} points.`}
									tooltipText=" "
									placement={tooltipPlacement.right}
								>
									<InfoOutlinedIcon style={{ fontSize: '1rem', marginLeft: 5 }} />
								</TooltipWraper>
							</Typography>
							<Typography
								variant={typographyVariants.heading2}
								color={colorsNew.light1}
								style={{ filter: dropShadow(colorsNew.primary) }}
							>
								{pointsToEarn !== undefined ? `+${pointsToEarn}` : 'Coming Soon'}
							</Typography>
						</div>
					)}
				</div>
			</div>
			<div className={classes.lootRow}>
				<Typography
					variant={typographyVariants.mini}
					color={colorsNew.light4}
					style={{ alignSelf: 'center' }}
				>LOOT:
				</Typography>
				<div className={classes.infoItem}>
					<Icon icon={faTreasureChest} size={32} />
					<div className={classes.lootGrid}>
						{!comingSoon ? (
							<>
								{map(
									item => (
										<div className={classes.lootItem}>
											<Typography
												variant={typographyVariants.miniBold}
												key={item}
											>
												{item}
											</Typography>
										</div>
									),
									take(3, loot),
								)}
							</>
						) : 'Coming soon'}
					</div>
				</div>
			</div>
			<div className={classes.slider}>
				<Typography
					variant={typographyVariants.micro}
					className={classes.sliderHeading}
				>Featured Dares:
				</Typography>
				<Slider
					containerClassName={classes.sliderContainer}
					arrows
					sliderWidth={40}
					infinite
					arrowsFixed
					showItems={1}
					renderSliderItem={() => identity}
				>
					{map(item => 	(
						<DareCardDrop
							hover={item.prizeAvailable && !item.userCreated}
							className={classes.card}
							dare={item}
							campaignId={campaignId}
							submitting={submitting}
							setSubmitting={setSubmitting}
							key={item.title + item.description}
							comingSoon={comingSoon}
							emailSubscription={emailSubscription}
							toggleEmailSubscription={toggleEmailSubscription}
							canAccessCreateBrandProject={canAccessCreateBrandProject}
							pointsToEarn={pointsToEarn}
							canEarnPoints={canEarnPoints}
							showTermsModal={showTermsModal}
							isAbleToParticipate={isAbleToParticipate}
						/>
					), dares)}
				</Slider>
			</div>
			<Anchor
				anchor={anchors.DROP_ALL_DARES}
				spy={false}
				classes={classes.allDaresText}
			>
				<Typography className={classes.link} color={null} variant={typographyVariants.smallBold}>
					See all Dares for {game}
				</Typography>
			</Anchor>
		</section>
	)
}

export default dropEventConnector(trackWindowScroll(DaresSlider), styles, 'MuiDaresSlider')
